// ============================ dashboard.slice.ts ============================

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IApiResponse } from "../sagas/dashboardSaga";

export interface DashboardLoadingState {
  vdiTransactions: boolean;
  /** Per-chart skeleton flags — each key flips false as its own API call resolves */
  vdiByType: Record<string, boolean>;
  smartCoolerProducts: boolean;
  alerts: boolean;
  quikPickProducts: boolean;
}

export interface DashboardState {
  dashboard: IApiResponse;
  loading: DashboardLoadingState;
  error: any;
}

export const initialState: DashboardState = {
  dashboard: {
    data: {
      vdiTransactions: { mmsDevices: [], mmsMarkets: [], mmsProducts: [], mmsSales: [] },
      alerts: [],
      quikPickMasterProducts: {
        total: 0,
        cvReady: 0,
      },
      smartCoolerProducts: { total: 0 },
      startDate: "",
      endDate: "",
    },
    success: false,
    message: "",
  },

  loading: {
    // All start as true so skeletons show from the very first render — no flash of empty content
    vdiTransactions: true,
    vdiByType: { mmsDevices: true, mmsMarkets: true, mmsProducts: true, mmsSales: true },
    smartCoolerProducts: true,
    alerts: true,
    quikPickProducts: false,
  },

  error: false,
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,

  reducers: {
    clearDashboardData: (state) => {
      state.dashboard = initialState.dashboard;
      state.loading = initialState.loading;
      state.error = false;
    },

    // VDI Transactions
    getVdiTransactionData: (state, _action: PayloadAction<any>) => {
      state.loading.vdiTransactions = true;
      // Reset every per-type flag so each chart shows skeleton on every refetch
      state.loading.vdiByType = { mmsDevices: true, mmsMarkets: true, mmsProducts: true, mmsSales: true };
      // Wipe stale data so no chart flashes old results while reloading
      state.dashboard.data.vdiTransactions = { mmsDevices: [], mmsMarkets: [], mmsProducts: [], mmsSales: [] };
      state.error = false;
    },

    // Per-type data is written by getVdiTypeSuccess; this only signals overall completion
    // so the VDI stat-card count (VdiCount) becomes valid.
    getVdiTransactionDataSuccess: (state, _action: PayloadAction<any>) => {
      state.loading.vdiTransactions = false;
    },

    getVdiTransactionDataFailure: (state, action: PayloadAction<any>) => {
      state.loading.vdiTransactions = false;
      // Clear any stuck per-type skeletons (outer saga catch fires before all types dispatch)
      state.loading.vdiByType = { mmsDevices: false, mmsMarkets: false, mmsProducts: false, mmsSales: false };
      state.error = action.payload || true;
    },

    /** Dispatched the moment an individual VDI type API call succeeds */
    getVdiTypeSuccess: (state, action: PayloadAction<{ type: string; data: any[] }>) => {
      state.loading.vdiByType[action.payload.type] = false;
      state.dashboard.data.vdiTransactions = {
        ...state.dashboard.data.vdiTransactions,
        [action.payload.type]: action.payload.data,
      };
    },

    /** Dispatched the moment an individual VDI type API call fails (other types keep loading) */
    getVdiTypeFailure: (state, action: PayloadAction<{ type: string }>) => {
      state.loading.vdiByType[action.payload.type] = false;
      // Data for this type stays as the empty array set in getVdiTransactionData
    },

    // Quikpick Products
    getQuikpickProductData: (state) => {
      state.loading.quikPickProducts = true;
      state.error = false;
    },
    getQuikpickProductDataSuccess: (state, action: PayloadAction<any>) => {
      state.loading.quikPickProducts = false;
      state.dashboard = {
        ...state.dashboard,
        data: {
          ...state.dashboard.data,
          quikPickMasterProducts: action?.payload?.data?.quikPickMasterProducts,
        },
      };
    },
    getQuikpickProductDataFailure: (state, action: PayloadAction<any>) => {
      state.loading.quikPickProducts = false;
      state.error = action.payload || true;
    },

    // Smart Cooler Products
    getSmartCoolerProductData: (state, _action: PayloadAction<any>) => {
      state.loading.smartCoolerProducts = true;
      state.error = false;
    },

    getSmartCoolerProductDataSuccess: (state, action: PayloadAction<any>) => {
      state.loading.smartCoolerProducts = false;
      state.dashboard = {
        ...state.dashboard,
        data: {
          ...state.dashboard.data,
          smartCoolerProducts: action?.payload?.data?.smartCoolerProducts,
        },
      };
    },

    getSmartCoolerProductDataFailure: (state, action: PayloadAction<any>) => {
      state.loading.smartCoolerProducts = false;
      state.error = action.payload || true;
    },

    // Alerts
    getAlertData: (state, _action: PayloadAction<any>) => {
      state.loading.alerts = true;
      state.error = false;
    },

    getAlertDataSuccess: (state, action: PayloadAction<any>) => {
      state.loading.alerts = false;
      state.dashboard = {
        ...state.dashboard,
        data: {
          ...state.dashboard.data,
          alerts: action?.payload?.data?.alerts ?? [],
        },
      };
    },
    getAlertDataFailure: (state, action: PayloadAction<any>) => {
      state.loading.alerts = false;
      state.error = action.payload || true;
    },
  },
});

const dashboardSliceReducer = dashboardSlice.reducer;

export const {
  clearDashboardData,
  getVdiTransactionData,
  getVdiTransactionDataSuccess,
  getVdiTransactionDataFailure,
  getVdiTypeSuccess,
  getVdiTypeFailure,
  getAlertData,
  getAlertDataSuccess,
  getAlertDataFailure,
  getQuikpickProductData,
  getQuikpickProductDataSuccess,
  getQuikpickProductDataFailure,
  getSmartCoolerProductData,
  getSmartCoolerProductDataSuccess,
  getSmartCoolerProductDataFailure,
} = dashboardSlice.actions;

export default dashboardSliceReducer;
